import { Injectable, NgZone, OnDestroy, inject } from "@angular/core";
import { BehaviorSubject, Subject, combineLatest, map } from "rxjs";
import { environment } from "@/environments/environment";
import { backend } from "@/integrations/backend/client";
import type { Tables, TablesUpdate } from "@/integrations/backend/types";
import {
  awardDailyStreakBonus,
  isDailyStreakWeekend,
} from "./daily-streak-bonus";
import { normalizeHub, type HubKey } from "./hubs";

export type Profile = Tables<"profiles">;
export interface ErrorLike {
  message: string;
}
export interface User {
  id: string;
  email?: string;
  user_metadata?: Record<string, unknown>;
}
export interface Session {
  access_token: string;
  refresh_token?: string;
  expires_at?: number;
  user: User;
}
export interface UserStreak {
  user_id: string;
  current_streak: number;
  longest_streak: number;
  last_seen_on: string | null;
  updated_at: string;
}

const AUTH_DEBUG_PREFIX = "[LINA][auth]";

function logAuthStep(step: string, details?: unknown) {
  if (!environment.production) {
    console.log(`${AUTH_DEBUG_PREFIX} ${step}`, details ?? "");
  }
}

function displayNameFor(user: User) {
  const metadataName = user.user_metadata?.["display_name"];
  if (typeof metadataName === "string" && metadataName.trim()) {
    return metadataName.trim();
  }

  return user.email?.split("@")[0] || "Explorer";
}

function hubFor(user: User): HubKey | null {
  const metadataHub = user.user_metadata?.["hub"];
  return typeof metadataHub === "string" ? normalizeHub(metadataHub) : null;
}

@Injectable({ providedIn: "root" })
export class AuthService implements OnDestroy {
  private readonly zone = inject(NgZone);
  private authSubscription?: { unsubscribe: () => void };
  private shouldCelebrateNextLogin = false;

  readonly session$ = new BehaviorSubject<Session | null>(null);
  readonly user$ = new BehaviorSubject<User | null>(null);
  readonly profile$ = new BehaviorSubject<Profile | null>(null);
  readonly loading$ = new BehaviorSubject<boolean>(true);
  readonly isAdmin$ = new BehaviorSubject<boolean | null>(null);
  readonly isCrewOwner$ = new BehaviorSubject<boolean>(false);
  readonly crewId$ = new BehaviorSubject<string | null>(null);
  readonly streak$ = new BehaviorSubject<UserStreak | null>(null);
  readonly loginCelebration$ = new Subject<UserStreak>();
  readonly isAuthenticated$ = this.user$.pipe(map(Boolean));

  readonly state$ = combineLatest([
    this.session$,
    this.user$,
    this.profile$,
    this.loading$,
    this.isAdmin$,
    this.isCrewOwner$,
    this.crewId$,
    this.streak$,
  ]).pipe(
    map(
      ([
        session,
        user,
        profile,
        loading,
        isAdmin,
        isCrewOwner,
        crewId,
        streak,
      ]) => ({
        session,
        user,
        profile,
        loading,
        isAdmin,
        isCrewOwner,
        crewId,
        streak,
        isAuthenticated: Boolean(user),
      }),
    ),
  );

  constructor() {
    logAuthStep("constructor:start");
    void this.initializeAuth();

    const { data: authListener } = backend.auth.onAuthStateChange(
      (_event: string, nextSession: Session | null) => {
        logAuthStep("onAuthStateChange", {
          event: _event,
          hasSession: Boolean(nextSession),
          userId: nextSession?.user?.id ?? null,
        });
        this.setSessionAndUser(nextSession, nextSession?.user ?? null);
        this.setLoading(true);

        window.setTimeout(() => {
          logAuthStep("onAuthStateChange:loadUserData:start", {
            userId: nextSession?.user?.id ?? null,
          });
          this.loadUserData(nextSession?.user ?? null)
            .catch((error) => {
              console.error("Error loading auth user data:", error);
              this.resetUserData();
            })
            .finally(() => {
              logAuthStep("onAuthStateChange:loadUserData:finish");
              this.setLoading(false);
            });
        }, 0);
      },
    );

    this.authSubscription = authListener.subscription;
  }

  async refreshUserData() {
    logAuthStep("refreshUserData:start", {
      userId: this.user$.value?.id ?? null,
    });
    this.setLoading(true);
    try {
      await this.loadUserData(this.user$.value);
    } finally {
      logAuthStep("refreshUserData:finish");
      this.setLoading(false);
    }
  }

  ngOnDestroy() {
    this.authSubscription?.unsubscribe();
  }

  async signIn(
    email: string,
    password: string,
  ): Promise<{ error: ErrorLike | null }> {
    logAuthStep("signIn:start", { email });
    this.shouldCelebrateNextLogin = true;
    const { error } = await backend.auth.signInWithPassword({
      email,
      password,
    });
    if (error) this.shouldCelebrateNextLogin = false;
    logAuthStep("signIn:finish", { hasError: Boolean(error) });
    return { error };
  }

  async signUp(
    email: string,
    password: string,
    displayName?: string,
    hub?: HubKey,
  ): Promise<{ error: ErrorLike | null }> {
    logAuthStep("signUp:start", { email, hub });
    const { error } = await backend.auth.signUp({
      email,
      password,
      options: {
        data: {
          display_name: displayName?.trim() || email.split("@")[0],
          hub,
        },
      },
    });

    logAuthStep("signUp:finish", { hasError: Boolean(error) });
    return { error };
  }

  async signOut(): Promise<{ error: ErrorLike | null }> {
    logAuthStep("signOut:start");
    const { error } = await backend.auth.signOut();

    this.shouldCelebrateNextLogin = false;
    this.setSessionAndUser(null, null);
    this.resetUserData();

    logAuthStep("signOut:finish", { hasError: Boolean(error) });
    return { error };
  }

  async updateProfile(
    updates: TablesUpdate<"profiles">,
  ): Promise<{ error: ErrorLike | null }> {
    const user = this.user$.value;
    if (!user) {
      return { error: new Error("No authenticated user") };
    }

    const { data, error } = await backend
      .from("profiles")
      .upsert(
        {
          id: user.id,
          ...updates,
        },
        { onConflict: "id" },
      )
      .select("*")
      .single();

    if (!error && data) {
      this.runInAngular(() => this.profile$.next(data));
    }

    return { error };
  }

  async updateEmail(email: string): Promise<{ error: ErrorLike | null }> {
    const { data, error } = await backend.auth.updateUser({ email });
    const updatedUser = data?.user as User | null | undefined;

    if (!error && updatedUser) {
      this.runInAngular(() => {
        this.user$.next(updatedUser);
        const currentSession = this.session$.value;
        if (currentSession) {
          this.session$.next({ ...currentSession, user: updatedUser });
        }
      });
    }

    return { error };
  }

  private async initializeAuth() {
    logAuthStep("initialize:start");
    this.setLoading(true);
    try {
      const { data, error } = await backend.auth.getSession();

      if (error) {
        console.error("Error loading auth session:", error);
      }

      const nextSession = data.session;
      logAuthStep("initialize:getSession:finish", {
        hasSession: Boolean(nextSession),
        hasError: Boolean(error),
        userId: nextSession?.user?.id ?? null,
      });

      if (nextSession) {
        logAuthStep("initialize:getUser:start");
        const { data: userData, error: userError } =
          await backend.auth.getUser();
        logAuthStep("initialize:getUser:finish", {
          hasUser: Boolean(userData.user),
          hasError: Boolean(userError),
          userId: userData.user?.id ?? null,
        });
        if (userError || !userData.user) {
          await this.discardLocalSession();
          return;
        }

        nextSession.user = userData.user;
      }

      this.setSessionAndUser(nextSession, nextSession?.user ?? null);
      logAuthStep("initialize:loadUserData:start", {
        userId: nextSession?.user?.id ?? null,
      });
      await this.loadUserData(nextSession?.user ?? null);
    } catch (error) {
      console.error("Error initializing auth:", error);
      await this.discardLocalSession();
    } finally {
      logAuthStep("initialize:finish");
      this.setLoading(false);
    }
  }

  private runInAngular(update: () => void) {
    if (NgZone.isInAngularZone()) {
      update();
      return;
    }

    this.zone.run(update);
  }

  private setLoading(value: boolean) {
    this.runInAngular(() => this.loading$.next(value));
  }

  private setSessionAndUser(session: Session | null, user: User | null) {
    this.runInAngular(() => {
      this.session$.next(session);
      this.user$.next(user);
    });
  }

  private resetUserData() {
    logAuthStep("resetUserData");
    this.runInAngular(() => {
      this.profile$.next(null);
      this.isAdmin$.next(false);
      this.isCrewOwner$.next(false);
      this.crewId$.next(null);
      this.streak$.next(null);
    });
  }

  private async discardLocalSession() {
    logAuthStep("discardLocalSession:start");
    await backend.auth.signOut();
    this.setSessionAndUser(null, null);
    this.resetUserData();
    logAuthStep("discardLocalSession:finish");
  }

  private async loadUserData(currentUser: User | null) {
    logAuthStep("loadUserData:start", {
      userId: currentUser?.id ?? null,
    });

    if (!currentUser) {
      this.resetUserData();
      logAuthStep("loadUserData:noUser");
      return;
    }

    logAuthStep("loadUserData:getUser:start");
    const { data: userData, error: userError } = await backend.auth.getUser();
    logAuthStep("loadUserData:getUser:finish", {
      hasUser: Boolean(userData.user),
      hasError: Boolean(userError),
      userId: userData.user?.id ?? null,
    });
    if (userError || !userData.user) {
      await this.discardLocalSession();
      return;
    }

    const verifiedUser = userData.user;

    if (
      verifiedUser.id !== currentUser.id ||
      verifiedUser.email !== currentUser.email
    ) {
      this.runInAngular(() => this.user$.next(verifiedUser));
      const currentSession = this.session$.value;
      if (currentSession) {
        this.runInAngular(() =>
          this.session$.next({ ...currentSession, user: verifiedUser }),
        );
      }
    }

    logAuthStep("loadUserData:profile:start", { userId: verifiedUser.id });
    const { data: profileData, error: profileError } = await backend
      .from("profiles")
      .select("*")
      .eq("id", verifiedUser.id)
      .maybeSingle();

    logAuthStep("loadUserData:profile:finish", {
      hasProfile: Boolean(profileData),
      hasError: Boolean(profileError),
    });

    if (profileError) {
      console.error("Error loading profile:", profileError);
      await this.discardLocalSession();
      return;
    } else if (profileData) {
      this.runInAngular(() => this.profile$.next(profileData));
    } else {
      logAuthStep("loadUserData:createProfile:start", {
        userId: verifiedUser.id,
      });
      const { data: createdProfile, error: createProfileError } = await backend
        .from("profiles")
        .upsert(
          {
            id: verifiedUser.id,
            display_name: displayNameFor(verifiedUser),
            hub: hubFor(verifiedUser),
          },
          { onConflict: "id" },
        )
        .select("*")
        .single();

      logAuthStep("loadUserData:createProfile:finish", {
        hasProfile: Boolean(createdProfile),
        hasError: Boolean(createProfileError),
      });

      if (createProfileError) {
        console.error("Error creating profile:", createProfileError);
        await this.discardLocalSession();
        return;
      } else {
        this.runInAngular(() => this.profile$.next(createdProfile));
      }
    }

    logAuthStep("loadUserData:adminRole:start");
    const { data: adminRole, error: roleError } = await backend
      .from("user_roles")
      .select("role")
      .eq("user_id", verifiedUser.id)
      .eq("role", "admin")
      .maybeSingle();

    logAuthStep("loadUserData:adminRole:finish", {
      isAdmin: Boolean(adminRole),
      hasError: Boolean(roleError),
    });

    if (roleError) {
      console.error("Error loading role:", roleError);
    }
    this.runInAngular(() => this.isAdmin$.next(Boolean(adminRole)));

    logAuthStep("loadUserData:crewMembership:start");
    const { data: membership, error: membershipError } = await backend
      .from("crew_members")
      .select("crew_id")
      .eq("user_id", verifiedUser.id)
      .limit(1)
      .maybeSingle();

    logAuthStep("loadUserData:crewMembership:finish", {
      crewId: membership?.crew_id ?? null,
      hasError: Boolean(membershipError),
    });

    if (membershipError) {
      console.error("Error loading crew membership:", membershipError);
    }

    logAuthStep("loadUserData:ownedCrew:start");
    const { data: ownedCrew, error: ownedCrewError } = await backend
      .from("crews")
      .select("id")
      .eq("owner_id", verifiedUser.id)
      .limit(1)
      .maybeSingle();

    logAuthStep("loadUserData:ownedCrew:finish", {
      crewId: ownedCrew?.id ?? null,
      hasError: Boolean(ownedCrewError),
    });

    if (ownedCrewError) {
      console.error("Error loading owned crew:", ownedCrewError);
    }

    this.runInAngular(() => {
      this.crewId$.next(membership?.crew_id || ownedCrew?.id || null);
      this.isCrewOwner$.next(Boolean(ownedCrew));
    });
    await this.recordDailyLogin();
    logAuthStep("loadUserData:finish");
  }

  private async recordDailyLogin() {
    try {
      logAuthStep("recordDailyLogin:start");
      const { data, error } = await backend.rpc("record_daily_login", {});

      if (error) throw error;
      const streak = Array.isArray(data) ? data[0] : data;
      const normalizedStreak = (streak as UserStreak | undefined) ?? null;
      if (normalizedStreak?.user_id) {
        await awardDailyStreakBonus(
          normalizedStreak.user_id,
          normalizedStreak.current_streak,
        );
      }
      this.runInAngular(() => this.streak$.next(normalizedStreak));
      this.consumeLoginCelebration(
        isDailyStreakWeekend() ? null : normalizedStreak,
      );
      logAuthStep("recordDailyLogin:finish", { hasStreak: Boolean(streak) });
    } catch (error) {
      console.warn("Daily streak could not be recorded; using fallback.", error);
      const currentUser = this.user$.value;
      if (currentUser?.id) {
        const storedStreak = await this.loadStoredStreak(currentUser.id);
        if (storedStreak) {
          this.consumeLoginCelebration(
            isDailyStreakWeekend() ? null : storedStreak,
          );
          return;
        }
      }
      this.consumeLoginCelebration(null);
      this.runInAngular(() => this.streak$.next(null));
    }
  }

  private consumeLoginCelebration(streak: UserStreak | null) {
    if (!this.shouldCelebrateNextLogin) return;

    this.shouldCelebrateNextLogin = false;
    if (!streak) return;

    this.runInAngular(() => this.loginCelebration$.next(streak));
  }

  private async loadStoredStreak(userId: string) {
    try {
      logAuthStep("loadStoredStreak:start", { userId });
      const { data, error } = await backend
        .from("user_streaks")
        .select(
          "user_id, current_streak, longest_streak, last_seen_on, updated_at",
        )
        .eq("user_id", userId)
        .maybeSingle();

      if (error) throw error;
      const storedStreak = (data as UserStreak | null) ?? null;
      this.runInAngular(() => this.streak$.next(storedStreak));
      logAuthStep("loadStoredStreak:finish", { hasStreak: Boolean(data) });
      return storedStreak;
    } catch (error) {
      console.error("Error loading stored daily streak:", error);
      return null;
    }
  }
}
